import _ from 'lodash';
import { rgba } from 'polished';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import Button from '~/components/Button';
import Icon from '~/components/Icon';
import { useOnClickOutside } from '~/hooks';
import { colors } from '~/styles';
import InlineTooltip from '../InlineTooltip';

const Arrow = styled.div`
  &,
  &::before {
    position: absolute;
    width: 8px;
    height: 8px;
    background: ${colors.white};
  }

  visibility: hidden;

  &::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }
`;

const Styled = {
  Trigger: styled.div`
    flex: 1;
    display: flex;
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;

    > ${Button} {
      height: 1.75rem;
      width: 1.75rem;
      padding: 0;
      border-radius: 0.25rem;
      background: transparent;
      color: ${colors.primary};
      transition: background 100ms ease-in-out;

      &:hover,
      &:focus,
      &:active,
      &:focus-within {
        background: ${colors.grey5};
      }

      .icon {
        font-size: 0.875rem;
      }
    }
  `,

  Menu: styled.div`
    min-width: 7.5rem;
    padding: 0.5rem 0;
    font-size: 0.75rem;
    border-radius: 0.3125rem;
    background: ${colors.white};
    border: solid 1px ${colors.grey10};
    box-shadow: 0 0.1875rem 1rem ${rgba(colors.black, 0.25)};
    z-index: 300;

    &[data-popper-placement^='top'] > ${Arrow} {
      bottom: -4px;
    }

    &[data-popper-placement^='bottom'] > ${Arrow} {
      top: -4px;
    }

    &[data-popper-placement^='left'] > ${Arrow} {
      right: -4px;
    }

    &[data-popper-placement^='right'] > ${Arrow} {
      left: -4px;
    }
  `,

  Item: styled(Button)`
    display: flex;
    align-items: center;
    padding: 0.25rem 1.25rem;
    border-radius: 0;
    width: 100%;
    justify-content: flex-start;
    color: ${colors.black};
    text-align: left;
    position: relative;

    &:hover {
      background-color: ${colors.grey5};
      color: ${colors.black};
    }

    &:disabled {
      cursor: not-allowed;

      &&&:hover {
        background: none;
        font-weight: normal;
        color: ${colors.black};
      }
    }
  `,

  Link: styled(Link)`
    display: flex;
    align-items: center;
    padding: 0.25rem 1.25rem;
    border-radius: 0;
    width: 100%;
    justify-content: flex-start;
    color: ${colors.black};
    text-align: left;
    position: relative;

    &:hover {
      background-color: ${colors.grey5};
      color: ${colors.black};
    }
  `,
};

export default function CardMenu({ children, ...props }) {
  const [isOpen, setIsOpen] = useState(() => !!props.isOpen);
  useEffect(() => {
    setIsOpen(props.isOpen);
  }, [props.isOpen]);

  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      { name: 'offset', options: { offset: [0, 0] } },
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: ['top', 'bottom'],
        },
      },
      { name: 'arrow', options: { element: arrowElement } },
    ],
    placement: 'auto',
  });

  const menuContentRef = useRef();
  useOnClickOutside(menuContentRef, () => setIsOpen(false));

  return (
    <Styled.Trigger ref={setReferenceElement} {...props} onClick={(e) => e.stopPropagation()}>
      <Button
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}>
        <Icon icon="ellipsis-h" />
      </Button>

      {isOpen &&
        ReactDOM.createPortal(
          <Styled.Menu ref={setPopperElement} style={styles.popper} {...attributes.popper}>
            <div ref={menuContentRef}>{typeof children === 'function' ? children({ setIsOpen }) : children}</div>
            <Arrow ref={setArrowElement} style={styles.arrow} />
          </Styled.Menu>,
          document.body,
        )}
    </Styled.Trigger>
  );
}

function Item({ disabled, tooltip, tooltipPlacement, children, onMount, ...props }) {
  const [state, setState] = useState(() => ({
    disabled: disabled,
    tooltip: tooltip,
    tooltipPlacement: tooltipPlacement,
  }));

  useEffect(() => {
    if (_.isFunction(onMount)) {
      onMount(setState);
    }
  }, [onMount]);

  return (
    <Styled.Item isAnchor {...props} disabled={state.disabled} onClick={state.disabled ? undefined : props.onClick}>
      {children}
      {state.tooltip && <InlineTooltip message={state.tooltip} placement={tooltipPlacement} />}
    </Styled.Item>
  );
}

CardMenu.Trigger = Styled.Trigger;
CardMenu.Item = Item;
CardMenu.Link = Styled.Link;
